import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  LayoutDashboard, Users, DollarSign, MessageSquare, Briefcase, 
  Calendar, Mail, LogOut, ExternalLink, Search, Phone, Clock,
  TrendingUp, FileText, Settings, Bell, ChevronRight, ChevronLeft,
  CheckCircle, AlertCircle, Star, Target, UserPlus, Building2,
  CalendarDays, Video, MoreHorizontal, Plus, Filter,
  Activity, BarChart3, Zap, ArrowRight, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { NotificationBell } from '../components/NotificationBell';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar metrics data - static placeholder figures for staff quick reference
const sidebarMetrics = [
  { label: 'Candidate Calls', value: '9/6', percent: 100, color: 'text-green-500', period: 'WEEKLY', source: 'Manatal' },
  { label: 'Client Calls', value: '9/30', percent: 30, color: 'text-blue-500', period: 'WEEKLY', source: 'Manual Entry' },
  { label: 'Screenings', value: '3/15', percent: 20, color: 'text-orange-500', period: 'WEEKLY', source: 'Manatal' },
  { label: 'Appointments', value: '1', percent: null, color: 'text-purple-500', period: 'MONTHLY', source: 'Manual Entry' },
  { label: 'Proposals', value: '5/3', percent: 100, color: 'text-green-500', period: 'MONTHLY', star: true, source: 'Manual Entry' },
  { label: 'Placements', value: '3/3', percent: 100, color: 'text-green-500', period: 'MONTHLY', star: true, source: 'Manatal' },
  { label: 'Active Jobs', value: '4/3', percent: 100, color: 'text-green-500', period: 'MONTHLY', star: true, source: 'Manatal' },
  { label: 'New Clients', value: '2/3', percent: 66, color: 'text-blue-500', period: 'MONTHLY', source: 'Manual Entry' },
];

// Quick access tools
const quickTools = [
  { name: 'Outlook Mail', url: 'https://outlook.office.com/mail/', icon: Mail },
  { name: 'Outlook Calendar', url: 'https://outlook.office.com/calendar/', icon: Calendar },
  { name: 'Omnia CRM', url: '/backoffice/intelligence/crm', icon: '👥' },
  { name: 'Manatal', url: 'https://app.manatal.com', icon: '🎯' },
  { name: 'LinkedIn', url: 'https://www.linkedin.com', icon: '💼' },
  { name: 'Sales Navigator', url: 'https://www.linkedin.com/sales', icon: '🧭' },
  { name: 'Instantly.ai', url: 'https://instantly.ai', icon: '⚡' },
  { name: 'Airtable', url: 'https://airtable.com', icon: '📊' },
];

const ZOHO_STATUS_STYLES = {
  connected: { label: 'Connected', className: 'bg-green-50 text-green-700 border-green-200' },
  authentication_error: { label: 'Authentication Error', className: 'bg-red-50 text-red-700 border-red-200' },
  permission_required: { label: 'Permission Required', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  scope_missing: { label: 'API Scope Missing', className: 'bg-orange-50 text-orange-700 border-orange-200' },
  plan_limitation: { label: 'Plan Limitation', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  sync_error: { label: 'Sync Error', className: 'bg-red-50 text-red-700 border-red-200' },
  not_configured: { label: 'Not Configured', className: 'bg-gray-100 text-gray-600 border-gray-300' },
  error: { label: 'Error', className: 'bg-red-50 text-red-700 border-red-200' },
};

const MANATAL_BADGE_STYLES = {
  connected: { label: 'Connected', className: 'bg-green-500/20 text-green-100 border-green-400/40' },
  not_configured: { label: 'Not Configured', className: 'bg-white/10 text-white/60 border-white/20' },
  authentication_error: { label: 'Error', className: 'bg-red-500/20 text-red-100 border-red-400/40' },
  error: { label: 'Error', className: 'bg-red-500/20 text-red-100 border-red-400/40' },
};

const ManatalHealthBadge = ({ health }) => {
  const state = health?.state || 'not_configured';
  const style = MANATAL_BADGE_STYLES[state] || MANATAL_BADGE_STYLES.error;
  return (
    <span
      title={health?.message || 'Manatal status unknown'}
      data-testid="manatal-health-badge"
      className={`text-xs px-2 py-0.5 rounded border flex items-center gap-1.5 ${style.className}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${state === 'connected' ? 'bg-green-400 animate-pulse' : state === 'not_configured' ? 'bg-gray-400' : 'bg-red-400'}`} />
      Manatal: {style.label}
    </span>
  );
};

const ZohoHealthBanner = ({ health, manatalHealth }) => {
  if (!health) return null;
  const modules = [
    { key: 'books', label: 'Zoho Books', info: health.books },
  ];
  if (manatalHealth) {
    modules.splice(1, 0, { key: 'manatal', label: 'Manatal', info: manatalHealth });
  }
  const hasIssue = modules.some((m) => m.info?.state !== 'connected');
  if (!hasIssue) return null;
  return (
    <div className="col-span-12" data-testid="zoho-integration-health-banner">
      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Integration Status</p>
        <div className="flex flex-wrap gap-2">
          {modules.map((m) => {
            const info = m.info || { state: 'sync_error', message: 'Unknown' };
            const style = ZOHO_STATUS_STYLES[info.state] || ZOHO_STATUS_STYLES.sync_error;
            return (
              <div
                key={m.key}
                title={info.message}
                data-testid={`zoho-status-${m.key}`}
                className={`text-xs px-3 py-1.5 rounded-lg border ${style.className} flex items-center gap-1.5`}
              >
                <span className="font-medium">{m.label}:</span> {style.label}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const BackOffice = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [zohoConnected, setZohoConnected] = useState(false);
  const [manatalMetrics, setManatalMetrics] = useState({});
  const [signContracts, setSignContracts] = useState({ signed_count: 0, pending_count: 0, pending_contracts: [] });
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [airtableSplits, setAirtableSplits] = useState({ splits: [], total_my_fees: 0, total_placement_fees: 0, count: 0 });
  const [airtableLoading, setAirtableLoading] = useState(true);
  const [submissionActivity, setSubmissionActivity] = useState({ today: { submitted: 0, interviews: 0, offers: 0, placements: 0 }, this_week: { submitted: 0, interviews: 0, offers: 0, placements: 0 } });
  const [pipelineHealth, setPipelineHealth] = useState({ pipeline: {}, total: 0 });
  const [revenueForecast, setRevenueForecast] = useState({ deals: [], total_forecast: 0, deal_count: 0 });
  const [placementProb, setPlacementProb] = useState({ active_searches: 0, candidates_submitted_30d: 0, client_interviews: 0, offers_extended: 0, predicted_placements: 0, estimated_revenue: 0, avg_placement_fee: 0, avg_days_to_placement: 0 });
  const [manatalPlacements, setManatalPlacements] = useState({ placements_this_month: 0, placements_this_year: 0, recent_placements: [] });
  const [activeJobOrdersCount, setActiveJobOrdersCount] = useState(0);
  const [zohoHealth, setZohoHealth] = useState(null);
  const [manatalHealth, setManatalHealth] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('backoffice_token');
    const userData = localStorage.getItem('backoffice_user');
    
    if (!token || !userData) {
      navigate('/backoffice/login');
      return;
    }

    setUser(JSON.parse(userData));

    fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (!res.ok) {
        localStorage.removeItem('backoffice_token');
        localStorage.removeItem('backoffice_user');
        navigate('/backoffice/login');
      }
    });

    // Check Zoho connection and fetch data
    fetchZohoData(token);
    fetchAirtableData(token);
    fetchManatalHealth(token);
    fetchManatalPlacements(token);
    fetchNativeCrmWidgets(token);
  }, [navigate]);

  // Phase 3 — Contract Tracker, Client Pipeline Health, Revenue Forecast and
  // Placement Probability now read from the native Omnia CRM instead of
  // Zoho CRM. Runs regardless of Zoho connection status (native DB is
  // always available). Zoho integration code/connection remains untouched.
  const fetchNativeCrmWidgets = async (token) => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [contractRes, pipelineRes, jobOrdersRes, forecastRes, probRes] = await Promise.all([
        fetch(`${API_URL}/api/crm/dashboard/contract-tracker`, { headers }),
        fetch(`${API_URL}/api/crm/pipeline`, { headers }),
        fetch(`${API_URL}/api/crm/job-orders?limit=1`, { headers }),
        fetch(`${API_URL}/api/crm/dashboard/revenue-forecast`, { headers }),
        fetch(`${API_URL}/api/crm/dashboard/placement-probability`, { headers }),
      ]);
      if (contractRes.ok) setSignContracts(await contractRes.json());
      if (pipelineRes.ok) {
        const p = await pipelineRes.json();
        const total = Object.values(p.stage_counts || {}).reduce((a, b) => a + b, 0);
        setPipelineHealth({ stage_counts: p.stage_counts || {}, total });
      }
      if (jobOrdersRes.ok) {
        const jo = await jobOrdersRes.json();
        const active = (jo.stage_counts?.['Intake'] || 0) + (jo.stage_counts?.['Active Search'] || 0);
        setActiveJobOrdersCount(active);
      }
      if (forecastRes.ok) setRevenueForecast(await forecastRes.json());
      if (probRes.ok) setPlacementProb(await probRes.json());
    } catch (error) {
      console.error('Error fetching native CRM dashboard widgets:', error);
    }
  };

  const fetchManatalHealth = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/manatal/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setManatalHealth(await res.json());
      }
    } catch (error) {
      console.error('Error fetching Manatal health:', error);
    }
  };

  const fetchManatalPlacements = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/manatal/placements?recent_limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setManatalPlacements(await res.json());
      }
    } catch (error) {
      console.error('Error fetching Manatal placements:', error);
    }
  };

  const fetchAirtableData = async (token) => {
    try {
      setAirtableLoading(true);
      const res = await fetch(`${API_URL}/api/airtable/splits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAirtableSplits(data);
      }
    } catch (error) {
      console.error('Error fetching Airtable data:', error);
    } finally {
      setAirtableLoading(false);
    }
  };

  const fetchZohoData = async (token) => {
    try {
      // Check Zoho status
      const statusRes = await fetch(`${API_URL}/api/zoho/status`);
      const statusData = await statusRes.json();
      setZohoConnected(statusData.connected);

      if (statusData.connected) {
        // Fetch ATS metrics (Manatal — read-only, gracefully not_configured without a token)
        const recruitRes = await fetch(`${API_URL}/api/manatal/dashboard-metrics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (recruitRes.ok) {
          const recruitData = await recruitRes.json();
          setManatalMetrics(recruitData);
        }

        // Sign contracts, Client Pipeline Health, Revenue Forecast and Placement
        // Probability are now native (see fetchNativeCrmWidgets) — no longer
        // fetched from Zoho here.

        // Fetch Zoho Bookings (upcoming meetings) — unrelated to CRM data, stays on Zoho
        const bookingsRes = await fetch(`${API_URL}/api/zoho/bookings/upcoming`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (bookingsRes.ok) {
          const bookingsData = await bookingsRes.json();
          setUpcomingMeetings(bookingsData.appointments || []);
        }

        // Fetch Candidate Submission Activity (Manatal)
        const activityRes = await fetch(`${API_URL}/api/manatal/submission-activity`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (activityRes.ok) {
          setSubmissionActivity(await activityRes.json());
        }

        // Fetch consolidated integration health (explicit states, never silent-zero)
        const healthRes = await fetch(`${API_URL}/api/zoho/integration-health`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (healthRes.ok) {
          setZohoHealth(await healthRes.json());
        }
      }
    } catch (error) {
      console.error('Error fetching Zoho data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('backoffice_token');
    localStorage.removeItem('backoffice_user');
    toast.success('Logged out successfully');
    navigate('/backoffice/login');
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentDate);
  const today = new Date();

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Left Sidebar - Metrics */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-56'} bg-[#0A1F44] text-white flex-shrink-0 transition-all duration-300`}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <img
                src="https://customer-assets.emergentagent.com/job_talent-delivery-2/artifacts/n21hqg7x_1000003920%20%281%29.png"
                alt="Omnia Paratus"
                className="h-8"
              />
            )}
            <button 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1 hover:bg-white/10 rounded"
            >
              {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {!sidebarCollapsed && (
          <div className="p-3 border-b border-white/10 space-y-2">
            <Link to="/backoffice/intelligence/daily-command" className="flex items-center gap-2 px-3 py-2.5 text-sm text-white bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-lg hover:bg-[#D4AF37]/30 transition-colors font-semibold" data-testid="daily-command-link">
              <Crown className="h-4 w-4 text-[#D4AF37]" /> Daily Command Center
            </Link>
            <Link to="/backoffice/intelligence" className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg hover:bg-[#D4AF37]/20 transition-colors">
              <BarChart3 className="h-4 w-4" /> Market Intelligence
            </Link>
          </div>
        )}

        {!sidebarCollapsed && (
          <div className="p-3 space-y-1">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Key Metrics</p>
            {sidebarMetrics.map((metric, idx) => (
              <div key={idx} className="bg-white/5 rounded-lg p-3" data-testid={`kpi-card-${metric.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-2xl font-bold text-white">{metric.value}</span>
                  {metric.star && <Star className="h-4 w-4 text-[#D4AF37] fill-[#D4AF37]" />}
                  {metric.percent === 100 && !metric.star && <CheckCircle className="h-4 w-4 text-green-400" />}
                </div>
                <p className="text-xs text-gray-400 uppercase">{metric.label}</p>
                <p className="text-[10px] text-gray-500">{metric.period}</p>
                {metric.percent !== null && (
                  <div className="mt-2 h-1 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${metric.percent >= 100 ? 'bg-green-500' : metric.percent >= 50 ? 'bg-blue-500' : 'bg-orange-500'}`}
                      style={{ width: `${Math.min(metric.percent, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="bg-[#1a7fc1] text-white px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <nav className="flex items-center gap-4 text-sm">
              <Link to="/backoffice/applications" className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors bg-white/10 px-3 py-1.5 rounded-lg">
                <FileText className="h-4 w-4" /> Applications
              </Link>
              <button className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors"
                onClick={() => navigate('/backoffice/intelligence/crm')}>
                <Building2 className="h-4 w-4" /> Clients
              </button>
              <button className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors"
                onClick={() => navigate('/backoffice/intelligence/crm?tab=pipeline')}>
                <CalendarDays className="h-4 w-4" /> CRM Activities
              </button>
              <button className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors"
                onClick={() => window.open('https://app.manatal.com', '_blank')}>
                <Users className="h-4 w-4" /> Candidates
              </button>
              <button className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors"
                onClick={() => window.open('https://app.manatal.com', '_blank')}>
                <Briefcase className="h-4 w-4" /> Job Openings
              </button>
              <Link to="/backoffice/tools" className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors">
                <Settings className="h-4 w-4" /> Tools
              </Link>
            </nav>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
              <input 
                type="text" 
                placeholder="Quick Search..." 
                className="pl-9 pr-4 py-1.5 bg-white/20 rounded-lg text-sm placeholder-white/60 focus:outline-none focus:bg-white/30 w-64"
              />
            </div>
            <NotificationBell />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0A1F44] font-bold text-sm">
                {user.name?.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm hidden lg:block">{user.name}</span>
            </div>
            <button onClick={handleLogout} className="p-2 hover:bg-white/10 rounded-lg">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Sub Header */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}, {user.name?.split(' ')[0]}</p>
              <h1 className="text-2xl font-bold text-[#0A1F44] flex items-center gap-2">
                Manage My Day <CheckCircle className="h-6 w-6 text-green-500" />
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-1"
                onClick={() => navigate('/backoffice/intelligence/crm')}
              >
                <Plus className="h-4 w-4" /> New CRM Activity
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-1"
                onClick={() => window.open('https://app.manatal.com', '_blank')}
              >
                <Plus className="h-4 w-4" /> New ATS Activity
              </Button>
              <Button 
                size="sm" 
                className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#c9a432] gap-1"
                onClick={() => window.open('https://zoom.us/meeting/schedule', '_blank')}
              >
                <Video className="h-4 w-4" /> Schedule Meeting
              </Button>
            </div>
          </div>
        </div>

        {/* Dashboard Grid */}
        <main className="flex-1 p-6 overflow-auto">
          <div className="grid grid-cols-12 gap-6">

            <ZohoHealthBanner health={zohoHealth} manatalHealth={manatalHealth} />

            {/* TODAY'S FOCUS PANEL - Full Width */}
            <div className="col-span-12">
              <Card className="border-2 border-[#D4AF37]/30 bg-gradient-to-r from-white to-[#D4AF37]/5">
                <CardHeader className="py-3 px-4 border-b border-[#D4AF37]/20">
                  <CardTitle className="text-lg font-bold text-[#0A1F44] flex items-center gap-2">
                    <Target className="h-5 w-5 text-[#D4AF37]" /> Today's Focus
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* New Leads to Contact */}
                    <div onClick={() => navigate('/backoffice/intelligence/weekly-targets')} className="flex items-center gap-3 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer" data-testid="focus-new-leads">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-700">5</p>
                        <p className="text-sm text-blue-600 font-medium">New Leads to Contact</p>
                      </div>
                    </div>

                    {/* Agency Partners to Message */}
                    <div onClick={() => navigate('/backoffice/intelligence/hiring-pressure')} className="flex items-center gap-3 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg hover:bg-yellow-100 transition-colors cursor-pointer" data-testid="focus-agency-partners">
                      <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-yellow-700">3</p>
                        <p className="text-sm text-yellow-600 font-medium">Agency Partners to Message</p>
                      </div>
                    </div>

                    {/* Candidates to Source */}
                    <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex items-center gap-3 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg hover:bg-green-100 transition-colors cursor-pointer" data-testid="focus-candidates">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-700">8</p>
                        <p className="text-sm text-green-600 font-medium">Candidates to Source</p>
                      </div>
                    </div>

                    {/* Follow-Ups Due */}
                    <div onClick={() => navigate('/backoffice/notifications')} className="flex items-center gap-3 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg hover:bg-red-100 transition-colors cursor-pointer" data-testid="focus-followups">
                      <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-700">4</p>
                        <p className="text-sm text-red-600 font-medium">Follow-Ups Due</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Quick Tools */}
            <div className="col-span-12 lg:col-span-3">
              <Card>
                <CardHeader className="bg-[#0A1F44] text-white rounded-t-lg py-3 px-4">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Settings className="h-4 w-4" /> Quick Access
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3">
                  <div className="grid grid-cols-2 gap-2">
                    {quickTools.map((tool) => (
                      <a
                        key={tool.name}
                        href={tool.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`quick-access-${tool.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="flex flex-col items-center p-3 bg-gray-50 rounded-lg hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] border border-transparent transition-all"
                      >
                        {typeof tool.icon === 'string' ? (
                          <span className="text-2xl mb-1">{tool.icon}</span>
                        ) : (
                          <tool.icon className="h-6 w-6 mb-1 text-[#0A1F44]" />
                        )}
                        <span className="text-xs font-medium text-[#0A1F44]">{tool.name}</span>
                      </a>
                    ))}
                  </div>
                  <Link 
                    to="/backoffice/tools" 
                    className="mt-3 block text-center text-sm text-[#1a7fc1] hover:underline"
                  >
                    View All Tools →
                  </Link>
                </CardContent>
              </Card>
            </div>

            {/* Agency Split Tracker - Revenue Focus */}
            <div className="col-span-12 lg:col-span-6">
              <Card className="border-2 border-purple-200">
                <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-lg py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <DollarSign className="h-4 w-4" /> Agency Split Tracker
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    </CardTitle>
                    <a 
                      href="https://airtable.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="px-2 py-0.5 bg-white/20 text-white text-xs rounded hover:bg-white/30 transition-colors flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3" /> Open in Airtable
                    </a>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-3" data-testid="airtable-splits-widget">
                  {airtableLoading ? (
                    <div className="text-center py-8 text-gray-500 text-sm">Loading splits...</div>
                  ) : airtableSplits.splits.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">No split deals found</div>
                  ) : (
                    <>
                      {airtableSplits.splits.slice(0, 5).map((split) => (
                        <div key={split.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-purple-50 transition-colors">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-sm text-[#0A1F44] truncate">{split.candidate_name}</p>
                              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] rounded font-medium flex-shrink-0">
                                {Math.round((split.split_my_agency || 0) * 100)}% / {Math.round((split.split_partner || 0) * 100)}%
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">{split.job_title}</p>
                            <p className="text-xs text-gray-400">{split.my_role}</p>
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                              split.deal_status === 'Closed' ? 'bg-green-100 text-green-700' : 
                              split.deal_status === 'Offer Extended' ? 'bg-blue-100 text-blue-700' : 
                              split.deal_status === 'Interview' || split.deal_status === 'Interviewing' ? 'bg-yellow-100 text-yellow-700' : 
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {split.deal_status}
                            </span>
                            <p className="text-sm font-bold text-green-600 mt-1">
                              ${(split.my_agency_fee || 0).toLocaleString()}
                            </p>
                            {split.invoice_sent && (
                              <span className="text-[10px] text-green-500">Invoiced</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {airtableSplits.count > 5 && (
                        <button className="w-full text-sm text-purple-600 hover:underline flex items-center justify-center gap-1 pt-1">
                          View all {airtableSplits.count} deals <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </>
                  )}
                  <div className="pt-2 border-t grid grid-cols-2 gap-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">My Agency Fees:</span>
                      <span className="text-sm font-bold text-green-600">${airtableSplits.total_my_fees.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Total Placements:</span>
                      <span className="text-sm font-bold text-[#0A1F44]">${airtableSplits.total_placement_fees.toLocaleString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Pipeline Snapshot - Momentum Tracker */}
            <div className="col-span-12 lg:col-span-6">
              <Card className="border-2 border-cyan-200">
                <CardHeader className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white rounded-t-lg py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" /> Pipeline Snapshot
                    </CardTitle>
                    <ManatalHealthBadge health={manatalHealth} />
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div onClick={() => navigate('/backoffice/intelligence/crm?tab=pipeline')} className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl cursor-pointer hover:shadow-md transition-all" data-testid="pipeline-clients">
                      <p className="text-3xl font-bold text-blue-600">{pipelineHealth.stage_counts?.['Active Client'] || 0}</p>
                      <p className="text-xs text-blue-700 font-medium mt-1">Active Clients</p>
                      <p className="text-[10px] text-blue-500">Omnia CRM</p>
                    </div>
                    <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl cursor-pointer hover:shadow-md transition-all" data-testid="pipeline-candidates">
                      <p className="text-3xl font-bold text-green-600">{manatalMetrics.total_candidates || 0}</p>
                      <p className="text-xs text-green-700 font-medium mt-1">Candidates</p>
                      <p className="text-[10px] text-green-500">Manatal</p>
                    </div>
                    <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl cursor-pointer hover:shadow-md transition-all" data-testid="pipeline-jobs">
                      <p className="text-3xl font-bold text-purple-600">{manatalMetrics.active_jobs || 0}</p>
                      <p className="text-xs text-purple-700 font-medium mt-1">Active Jobs</p>
                      <p className="text-[10px] text-purple-500">Manatal</p>
                    </div>
                  </div>
                  
                  {/* Pipeline Progress Bar */}
                  <div className="space-y-3">
                    <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="cursor-pointer hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">Candidates</span>
                        <span className="font-medium text-gray-700">{manatalMetrics.total_candidates || 0} total</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: manatalMetrics.total_candidates ? '80%' : '0%' }}></div>
                      </div>
                    </div>
                    <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="cursor-pointer hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">Screenings</span>
                        <span className="font-medium text-gray-700">{manatalMetrics.screenings || 0} scheduled</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-yellow-500 rounded-full" style={{ width: manatalMetrics.screenings ? '50%' : '0%' }}></div>
                      </div>
                    </div>
                    <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="cursor-pointer hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">Active Jobs</span>
                        <span className="font-medium text-gray-700">{manatalMetrics.active_jobs || 0} open</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: manatalMetrics.active_jobs ? '100%' : '0%' }}></div>
                      </div>
                    </div>
                    <div onClick={() => navigate('/backoffice/intelligence/crm?tab=agreements')} className="cursor-pointer hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">Agreements Pending</span>
                        <span className="font-medium text-gray-700">{signContracts.pending_count || 0} sent</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: signContracts.pending_count ? '30%' : '0%' }}></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contract Tracker — native Omnia CRM Agreements (Phase 3) */}
            <div className="col-span-12 lg:col-span-6">
              <Card className="border-2 border-indigo-200">
                <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-t-lg py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Agreement Tracker
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    </CardTitle>
                    <a
                      href="/backoffice/intelligence/crm?tab=agreements"
                      className="px-2 py-0.5 bg-white/20 text-white text-xs rounded hover:bg-white/30 transition-colors flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3" /> Omnia CRM
                    </a>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div onClick={() => navigate('/backoffice/intelligence/crm?tab=agreements')} className="text-center p-4 bg-green-50 border border-green-200 rounded-xl cursor-pointer hover:bg-green-100 transition-colors">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <CheckCircle className="h-6 w-6 text-white" />
                      </div>
                      <p className="text-3xl font-bold text-green-600">{signContracts.signed_count}</p>
                      <p className="text-sm text-green-700 font-medium">Signed</p>
                    </div>
                    <div onClick={() => navigate('/backoffice/intelligence/crm?tab=agreements')} className="text-center p-4 bg-orange-50 border border-orange-200 rounded-xl cursor-pointer hover:bg-orange-100 transition-colors">
                      <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Clock className="h-6 w-6 text-white" />
                      </div>
                      <p className="text-3xl font-bold text-orange-600">{signContracts.pending_count}</p>
                      <p className="text-sm text-orange-700 font-medium">Pending</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">Pending Signatures</p>
                    {signContracts.pending_contracts && signContracts.pending_contracts.length > 0 ? (
                      signContracts.pending_contracts.map((contract, idx) => (
                        <div key={idx} onClick={() => navigate('/backoffice/intelligence/crm?tab=agreements')} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                          <div>
                            <p className="text-sm font-medium text-[#0A1F44]">{contract.name}</p>
                            <p className="text-xs text-gray-500">{contract.status}</p>
                          </div>
                          <div className="text-right">
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">Awaiting</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No pending agreements
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* === NEW WIDGETS === */}

            {/* 1. Candidate Submission Activity */}
            <div className="col-span-12 lg:col-span-6" data-testid="submission-activity-widget">
              <Card className="border-2 border-teal-200">
                <CardHeader className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-t-lg py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Activity className="h-4 w-4" /> Candidate Submission Activity
                    </CardTitle>
                    <ManatalHealthBadge health={manatalHealth} />
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Today */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Today</p>
                      <div className="space-y-3">
                        <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                          <span className="text-sm text-gray-700">Submitted to Clients</span>
                          <span className="text-lg font-bold text-blue-600">{submissionActivity.today.submitted}</span>
                        </div>
                        <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors">
                          <span className="text-sm text-gray-700">Interviews Scheduled</span>
                          <span className="text-lg font-bold text-yellow-600">{submissionActivity.today.interviews}</span>
                        </div>
                        <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors">
                          <span className="text-sm text-gray-700">Offers Extended</span>
                          <span className="text-lg font-bold text-orange-600">{submissionActivity.today.offers}</span>
                        </div>
                        <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex items-center justify-between p-2 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
                          <span className="text-sm text-gray-700">Placements Closed</span>
                          <span className="text-lg font-bold text-green-600">{submissionActivity.today.placements}</span>
                        </div>
                      </div>
                    </div>
                    {/* This Week */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">This Week</p>
                      <div className="space-y-3">
                        <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                          <span className="text-sm text-gray-700">Submitted to Clients</span>
                          <span className="text-lg font-bold text-blue-600">{submissionActivity.this_week.submitted}</span>
                        </div>
                        <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors">
                          <span className="text-sm text-gray-700">Interviews Scheduled</span>
                          <span className="text-lg font-bold text-yellow-600">{submissionActivity.this_week.interviews}</span>
                        </div>
                        <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors">
                          <span className="text-sm text-gray-700">Offers Extended</span>
                          <span className="text-lg font-bold text-orange-600">{submissionActivity.this_week.offers}</span>
                        </div>
                        <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex items-center justify-between p-2 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
                          <span className="text-sm text-gray-700">Placements Closed</span>
                          <span className="text-lg font-bold text-green-600">{submissionActivity.this_week.placements}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* 2. Client Pipeline Health — native Omnia CRM pipeline_stage (Phase 3) */}
            <div className="col-span-12 lg:col-span-6" data-testid="pipeline-health-widget">
              <Card className="border-2 border-sky-200">
                <CardHeader className="bg-gradient-to-r from-sky-600 to-sky-700 text-white rounded-t-lg py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" /> Client Pipeline Health
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    </CardTitle>
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded">Omnia CRM</span>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {(() => {
                    const p = pipelineHealth.stage_counts || {};
                    const stages = [
                      { key: 'Prospect', label: 'Prospect', color: 'bg-slate-500', bgLight: 'bg-slate-50', text: 'text-slate-700' },
                      { key: 'Outreach', label: 'Outreach', color: 'bg-blue-400', bgLight: 'bg-blue-50', text: 'text-blue-700' },
                      { key: 'Response', label: 'Response', color: 'bg-blue-500', bgLight: 'bg-blue-50', text: 'text-blue-700' },
                      { key: 'Discovery', label: 'Discovery', color: 'bg-indigo-400', bgLight: 'bg-indigo-50', text: 'text-indigo-700' },
                      { key: 'Agreement Sent', label: 'Agreement Sent', color: 'bg-amber-400', bgLight: 'bg-amber-50', text: 'text-amber-700' },
                      { key: 'Agreement Signed', label: 'Agreement Signed', color: 'bg-amber-600', bgLight: 'bg-amber-50', text: 'text-amber-700' },
                      { key: 'Active Client', label: 'Active Client', color: 'bg-indigo-600', bgLight: 'bg-indigo-50', text: 'text-indigo-700' },
                      { key: 'Job Order', label: 'Job Order', color: 'bg-orange-500', bgLight: 'bg-orange-50', text: 'text-orange-700' },
                      { key: 'Placement / Won', label: 'Placement / Won', color: 'bg-green-500', bgLight: 'bg-green-50', text: 'text-green-700' },
                      { key: 'Lost / Closed', label: 'Lost / Closed', color: 'bg-gray-400', bgLight: 'bg-gray-50', text: 'text-gray-600' },
                    ];
                    return (
                      <div className="space-y-3">
                        {/* Horizontal pipeline bar */}
                        <div className="flex rounded-lg overflow-hidden h-8 bg-gray-100">
                          {stages.map((stage) => {
                            const val = p[stage.key] || 0;
                            const pct = pipelineHealth.total > 0 ? Math.max((val / pipelineHealth.total) * 100, val > 0 ? 4 : 0) : (100 / stages.length);
                            return (
                              <div
                                key={stage.key}
                                className={`${stage.color} flex items-center justify-center text-white text-[10px] font-bold transition-all`}
                                style={{ width: `${pct}%`, minWidth: val > 0 ? '22px' : '0' }}
                                title={`${stage.label}: ${val}`}
                              >
                                {val > 0 && val}
                              </div>
                            );
                          })}
                        </div>
                        {/* Stage breakdown */}
                        <div className="grid grid-cols-2 gap-2 mt-3">
                          {stages.map((stage) => (
                            <div key={stage.key} onClick={() => navigate('/backoffice/intelligence/crm?tab=pipeline')} className={`flex items-center justify-between p-2 ${stage.bgLight} rounded-lg cursor-pointer hover:shadow-sm transition-all`}>
                              <div className="flex items-center gap-2">
                                <div className={`w-2.5 h-2.5 rounded-full ${stage.color}`}></div>
                                <span className="text-xs text-gray-600">{stage.label}</span>
                              </div>
                              <span className={`text-sm font-bold ${stage.text}`}>{p[stage.key] || 0}</span>
                            </div>
                          ))}
                        </div>
                        <div className="pt-2 border-t flex items-center justify-center gap-6">
                          <span data-testid="pipeline-total-companies">
                            <span className="text-sm text-gray-600">Total Companies: </span>
                            <span className="text-sm font-bold text-[#0A1F44]">{pipelineHealth.total}</span>
                          </span>
                          <span data-testid="active-job-orders-metric">
                            <span className="text-sm text-gray-600">Active Job Orders: </span>
                            <span className="text-sm font-bold text-[#0A1F44]">{activeJobOrdersCount}</span>
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>

            {/* Manatal Placements — hired candidates only (hired_at set), never submissions/interviews/offers */}
            <div className="col-span-12" data-testid="manatal-placements-widget">
              <Card className="border-2 border-fuchsia-200">
                <CardHeader className="bg-gradient-to-r from-fuchsia-600 to-fuchsia-700 text-white rounded-t-lg py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Crown className="h-4 w-4" /> Manatal Placements
                    </CardTitle>
                    <ManatalHealthBadge health={manatalHealth} />
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-4 bg-fuchsia-50 border border-fuchsia-200 rounded-xl" data-testid="placements-this-month">
                      <p className="text-3xl font-bold text-fuchsia-700">{manatalPlacements.placements_this_month}</p>
                      <p className="text-sm text-fuchsia-600 font-medium mt-1">Placements This Month</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 border border-purple-200 rounded-xl" data-testid="placements-this-year">
                      <p className="text-3xl font-bold text-purple-700">{manatalPlacements.placements_this_year}</p>
                      <p className="text-sm text-purple-600 font-medium mt-1">Placements This Year</p>
                    </div>
                  </div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Recent Placements</p>
                  {manatalPlacements.recent_placements.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No placements recorded in Manatal yet — hires will appear here as soon as a candidate's hired_at is set.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Candidate</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Job</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Hired</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Stage</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Compensation</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Fee</th>
                          </tr>
                        </thead>
                        <tbody>
                          {manatalPlacements.recent_placements.map((p) => (
                            <tr key={p.match_id} className="border-b border-gray-100 hover:bg-fuchsia-50/50 transition-colors">
                              <td className="py-2.5 px-3 font-medium text-[#0A1F44]">{p.candidate_name || 'Unknown'}</td>
                              <td className="py-2.5 px-3 text-gray-600">{p.job_title || 'Unknown'}</td>
                              <td className="py-2.5 px-3 text-gray-600">{p.client || 'Unknown'}</td>
                              <td className="py-2.5 px-3 text-gray-600">{p.hired_at ? p.hired_at.slice(0, 10) : '—'}</td>
                              <td className="py-2.5 px-3">
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium">{p.stage || 'Unknown'}</span>
                              </td>
                              <td className="py-2.5 px-3 text-right font-medium">
                                {p.compensation?.type === 'range'
                                  ? `$${p.compensation.min.toLocaleString()}–$${p.compensation.max.toLocaleString()} ${p.compensation.currency || ''}`
                                  : p.compensation?.type === 'single'
                                  ? `$${p.compensation.min.toLocaleString()} ${p.compensation.currency || ''}`
                                  : 'Compensation Needed'}
                              </td>
                              <td className="py-2.5 px-3 text-right font-bold text-fuchsia-700">
                                {p.fee?.type === 'range' ? (
                                  <span title={p.fee.label}>${p.fee.min.toLocaleString()}–${p.fee.max.toLocaleString()}</span>
                                ) : p.fee?.type === 'single' ? (
                                  <span title={p.fee.label}>${p.fee.min.toLocaleString()}</span>
                                ) : (
                                  <span className="text-gray-400 font-normal text-xs">{p.fee?.label}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* 3. Revenue Forecast — native Omnia CRM Job Orders (Phase 3) */}
            <div className="col-span-12" data-testid="revenue-forecast-widget">
              <Card className="border-2 border-emerald-200">
                <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-t-lg py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" /> Revenue Forecast
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded">Omnia CRM</span>
                      <div className="text-right">
                        <p className="text-xs text-emerald-200">Projected Placement Revenue</p>
                        <p className="text-xl font-bold">${revenueForecast.total_forecast.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {revenueForecast.deals.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No open Job Orders with an estimated fee yet. Forecast will appear here as Job Orders are created in the CRM.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Client</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Role / Job Order</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Stage</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Placement Fee</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Probability</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Forecast</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueForecast.deals.slice(0, 10).map((deal, idx) => (
                            <tr key={idx} onClick={() => navigate('/backoffice/intelligence/crm?tab=job-orders')} className="border-b border-gray-100 hover:bg-emerald-50/50 transition-colors cursor-pointer">
                              <td className="py-2.5 px-3 font-medium text-[#0A1F44]">{deal.client_name}</td>
                              <td className="py-2.5 px-3 text-gray-600">{deal.deal_name}</td>
                              <td className="py-2.5 px-3">
                                <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                                  deal.stage_category === 'Filled' ? 'bg-green-100 text-green-700' :
                                  deal.stage_category === 'Active Search' ? 'bg-indigo-100 text-indigo-700' :
                                  deal.stage_category === 'Intake' ? 'bg-amber-100 text-amber-700' :
                                  deal.stage_category === 'On Hold' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {deal.stage || 'Unknown'}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-right font-medium">${deal.placement_fee.toLocaleString()}</td>
                              <td className="py-2.5 px-3 text-right">
                                <span className={`font-medium ${deal.probability >= 0.8 ? 'text-green-600' : deal.probability >= 0.5 ? 'text-amber-600' : 'text-gray-600'}`}>
                                  {Math.round(deal.probability * 100)}%
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-right font-bold text-emerald-600">${deal.forecast_revenue.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {revenueForecast.deal_count > 10 && (
                        <p className="text-center text-sm text-gray-500 mt-2">Showing top 10 of {revenueForecast.deal_count} Job Orders</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* 4. Placement Probability Engine — native Omnia CRM + Manatal (Phase 3) */}
            <div className="col-span-12 lg:col-span-6" data-testid="placement-probability-widget">
              <Card className="border-2 border-violet-200">
                <CardHeader className="bg-gradient-to-r from-violet-600 to-violet-700 text-white rounded-t-lg py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Zap className="h-4 w-4" /> Placement Probability Engine
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    </CardTitle>
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded">Omnia CRM + Manatal</span>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {/* Main Predictions */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div onClick={() => navigate('/backoffice/intelligence/crm?tab=job-orders')} className="text-center p-4 bg-violet-50 border-2 border-violet-200 rounded-xl cursor-pointer hover:bg-violet-100 transition-colors">
                      <p className="text-4xl font-bold text-violet-600">{placementProb.predicted_placements}</p>
                      <p className="text-sm text-violet-700 font-medium mt-1">Predicted Placements</p>
                      <p className="text-xs text-violet-500">Next 30 Days</p>
                    </div>
                    <div onClick={() => navigate('/backoffice/intelligence/crm?tab=revenue')} className="text-center p-4 bg-emerald-50 border-2 border-emerald-200 rounded-xl cursor-pointer hover:bg-emerald-100 transition-colors">
                      <p className="text-4xl font-bold text-emerald-600">${placementProb.estimated_revenue.toLocaleString()}</p>
                      <p className="text-sm text-emerald-700 font-medium mt-1">Estimated Revenue</p>
                      <p className="text-xs text-emerald-500">Forecast</p>
                    </div>
                  </div>

                  {/* Supporting Metrics */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div onClick={() => navigate('/backoffice/intelligence/crm?tab=revenue')} className="text-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <p className="text-2xl font-bold text-[#0A1F44]">${placementProb.avg_placement_fee.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Avg Placement Fee</p>
                    </div>
                    <div onClick={() => navigate('/backoffice/intelligence/crm?tab=revenue')} className="text-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <p className="text-2xl font-bold text-[#0A1F44]">{placementProb.avg_days_to_placement}</p>
                      <p className="text-xs text-gray-500">Avg Days to Place</p>
                    </div>
                    <div onClick={() => navigate('/backoffice/intelligence/crm?tab=job-orders')} className="text-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <p className="text-2xl font-bold text-[#0A1F44]">{placementProb.active_searches}</p>
                      <p className="text-xs text-gray-500">Active Searches</p>
                    </div>
                  </div>

                  {/* Conversion Pipeline */}
                  <div className="border-t pt-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Pipeline Activity (30 Days)</p>
                    <div className="flex items-center justify-between">
                      <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex-1 text-center cursor-pointer hover:opacity-80 transition-opacity">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-1">
                          <span className="text-lg font-bold text-blue-600">{placementProb.candidates_submitted_30d}</span>
                        </div>
                        <p className="text-[10px] text-gray-500">Submitted</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                      <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex-1 text-center cursor-pointer hover:opacity-80 transition-opacity">
                        <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-1">
                          <span className="text-lg font-bold text-yellow-600">{placementProb.client_interviews}</span>
                        </div>
                        <p className="text-[10px] text-gray-500">Interviews</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                      <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex-1 text-center cursor-pointer hover:opacity-80 transition-opacity">
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-1">
                          <span className="text-lg font-bold text-orange-600">{placementProb.offers_extended}</span>
                        </div>
                        <p className="text-[10px] text-gray-500">Offers</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                      <div onClick={() => window.open('https://app.manatal.com', '_blank')} className="flex-1 text-center cursor-pointer hover:opacity-80 transition-opacity">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1">
                          <span className="text-lg font-bold text-green-600">{placementProb.predicted_placements}</span>
                        </div>
                        <p className="text-[10px] text-gray-500">Predicted</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default BackOffice;
